import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Card from "../components/Card.jsx";
import Modal from "../components/Modal.jsx";
import { connectionApi } from "../../api/connectionApi";
import { getApiError } from "../../api/client";
import { Plus, PlugZap, Settings2 } from "lucide-react";
import "../styles/codebase.css";

function displayBaseUrl(row) {
  const p = String(row?.provider || "").toLowerCase();
  const settings = row?.settings_json || {};
  if (p === "salesforce") {
    return settings?.sfdc_login_url || settings?.SFDC_LOGIN_URL || "—";
  }
  if (p === "azure_devops") {
    const org = settings?.org || "—";
    return `https://dev.azure.com/${org}`;
  }
  return row?.base_url || "—";
}

function providerHelp(provider) {
  const p = String(provider || "").toLowerCase();
  if (p === "salesforce") return "Override: SFDC_LOGIN_URL, SFDC_API_VERSION + username/password/security token (SOAP login).";
  if (p === "servicenow") return "Override: base_url + token OR username/password.";
  if (p === "sap") return "Override: base_url + username/password + (health_path, verify_ssl).";
  if (p === "oracle_apps") return "Override: base_url + username/password + (test_path, verify_ssl).";
  if (p === "azure_devops") return "Override: org + PAT.";
  if (p === "github") return "Override: token.";
  return "You can optionally provide transient overrides for test (not saved).";
}

export default function ConnectionsListPage() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  // advanced test modal
  const [testModal, setTestModal] = useState(false);
  const [testRow, setTestRow] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [testPayload, setTestPayload] = useState({
    base_url: "",
    username: "",
    settings_json: {},
    secret_json: {},
  });

  const load = async () => {
    setErr("");
    setBusy(true);
    try {
      const res = await connectionApi.list(false);
      setRows(res.data || []);
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const quickTest = async (id) => {
    setErr("");
    try {
      const res = await connectionApi.test(id);
      const ok = !!res.data?.ok;
      const detail = res.data?.detail || "";
      const meta = res.data?.meta ? JSON.stringify(res.data.meta, null, 2) : "";
      alert(`${ok ? "✅" : "❌"} ${detail}${meta ? `\n\nMeta:\n${meta}` : ""}`);
    } catch (e) {
      setErr(getApiError(e));
    }
  };

  const openAdvancedTest = (row) => {
    setErr("");
    setTestRow(row);
    setTestPayload({
      base_url: row?.base_url || "",
      username: row?.username || "",
      settings_json: row?.settings_json || {},
      secret_json: {}, // NEVER prefill secrets
    });
    setTestModal(true);
  };

  const runAdvancedTest = async () => {
    if (!testRow?.id) return;
    setErr("");
    setTestBusy(true);
    try {
      const payload = {
        base_url: testPayload.base_url || null,
        username: testPayload.username || null,
        settings_json: testPayload.settings_json || {},
        secret_json: testPayload.secret_json || {},
      };
      const res = await connectionApi.test(testRow.id, payload);

      const ok = !!res.data?.ok;
      const detail = res.data?.detail || "";
      const meta = res.data?.meta ? JSON.stringify(res.data.meta, null, 2) : "";
      alert(`${ok ? "✅" : "❌"} ${detail}${meta ? `\n\nMeta:\n${meta}` : ""}`);
    } catch (e) {
      setErr(getApiError(e));
    } finally {
      setTestBusy(false);
    }
  };

  const providerSummary = useMemo(() => {
    const counts = {};
    for (const r of rows) {
      const p = String(r.provider || "unknown");
      counts[p] = (counts[p] || 0) + 1;
    }
    return counts;
  }, [rows]);

  return (
    <div className="cb-page">
      <div className="page-head">
        <div>
          <h2>Connections</h2>
          <div className="muted">
            Configure SAP / Oracle Apps / Salesforce / ServiceNow / Azure DevOps / GitHub
          </div>
          <div className="muted small" style={{ marginTop: 6 }}>
            {Object.keys(providerSummary).length
              ? Object.entries(providerSummary)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" • ")
              : ""}
          </div>
        </div>
        <div className="page-actions">
          <Link className="btn primary" to="/codebase/connections/new">
            <Plus size={16} /> Add Connection
          </Link>
          <button className="btn" onClick={load} disabled={busy}>
            {busy ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {err && <div className="error">{err}</div>}

      <Card title="Saved connection configurations">
        <div className="table-wrap">
          <table className="cb-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Provider</th>
                <th>Base / Login URL</th>
                <th>Status</th>
                <th style={{ width: 320 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="muted">
                    No connections yet.
                  </td>
                </tr>
              ) : (
                rows.map((c) => (
                  <tr key={c.id}>
                    <td className="mono">{c.name}</td>
                    <td>{c.provider}</td>
                    <td className="mono small">{displayBaseUrl(c)}</td>
                    <td>{c.is_active ? "Active" : "Inactive"}</td>
                    <td style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="btn" onClick={() => quickTest(c.id)}>
                        <PlugZap size={16} /> Test
                      </button>

                      <button className="btn ghost" onClick={() => openAdvancedTest(c)}>
                        <Settings2 size={16} /> Advanced Test
                      </button>

                      <Link className="btn ghost" to={`/codebase/connections/${c.id}/edit`}>
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Advanced Test Modal */}
      <Modal
        open={testModal}
        title={`Advanced Test: ${testRow?.name || ""}`}
        onClose={() => setTestModal(false)}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="btn ghost" onClick={() => setTestModal(false)} disabled={testBusy}>
              Close
            </button>
            <button className="btn primary" onClick={runAdvancedTest} disabled={testBusy}>
              {testBusy ? "Testing…" : "Run Test"}
            </button>
          </div>
        }
      >
        <div className="form">
          <div className="muted small" style={{ marginBottom: 10 }}>
            {providerHelp(testRow?.provider)}
          </div>

          <div className="grid-2">
            <div className="field">
              <label>Base URL (override)</label>
              <input
                value={testPayload.base_url}
                onChange={(e) => setTestPayload((p) => ({ ...p, base_url: e.target.value }))}
                placeholder="optional"
              />
            </div>
            <div className="field">
              <label>Username (override)</label>
              <input
                value={testPayload.username}
                onChange={(e) => setTestPayload((p) => ({ ...p, username: e.target.value }))}
                placeholder="optional"
              />
            </div>
          </div>

          <div className="field">
            <label>settings_json (override)</label>
            <textarea
              rows={6}
              value={JSON.stringify(testPayload.settings_json || {}, null, 2)}
              onChange={(e) => {
                try {
                  const obj = JSON.parse(e.target.value || "{}");
                  setTestPayload((p) => ({ ...p, settings_json: obj }));
                } catch {
                  // ignore invalid JSON until user fixes
                }
              }}
              placeholder='e.g. { "sfdc_login_url": "https://login.salesforce.com", "sfdc_api_version": "60.0" }'
            />
            <div className="hint">Non-secret config. JSON only.</div>
          </div>

          <div className="field">
            <label>secret_json (override)</label>
            <textarea
              rows={6}
              value={JSON.stringify(testPayload.secret_json || {}, null, 2)}
              onChange={(e) => {
                try {
                  const obj = JSON.parse(e.target.value || "{}");
                  setTestPayload((p) => ({ ...p, secret_json: obj }));
                } catch {
                  // ignore invalid JSON until user fixes
                }
              }}
              placeholder='e.g. { "sfdc_username": "...", "sfdc_password": "...", "sfdc_security_token": "..." }'
            />
            <div className="hint">Secrets are NOT saved by this test call.</div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
